import React, { useEffect, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  DRILLDOWN_COLLAPSE_TRIGGER_SX,
} from './analyticsDrilldownStyles';

const AboutBody = ({ body, sections }) => (
  <>
    {body ? (
      <Typography
        sx={{
          fontSize: '0.78rem',
          color: 'rgba(0,0,0,0.66)',
          lineHeight: 1.55,
        }}
      >
        {body}
      </Typography>
    ) : null}
    {(sections || []).map((s, i) => (
      <Box key={`${s.label}-${i}`} sx={{ mt: i === 0 && !body ? 0 : 1.25 }}>
        <Typography
          sx={{
            fontSize: '0.65rem',
            fontWeight: 600,
            color: 'rgba(0,0,0,0.5)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            mb: 0.4,
          }}
        >
          {s.label}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.78rem',
            color: 'rgba(0,0,0,0.66)',
            lineHeight: 1.55,
          }}
        >
          {s.text}
        </Typography>
      </Box>
    ))}
  </>
);

const expandedTriggerSx = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  p: 0,
  mb: 1.25,
  borderRadius: '0 !important',
  border: 'none',
  bgcolor: 'transparent',
  textTransform: 'none !important',
  '&:hover': { bgcolor: 'transparent' },
};

/**
 * Collapsible "About this metric" block for drill-down sheets — trigger
 * styling matches the dashboard "See activity" row. When expanded, trigger
 * and body share one bordered container.
 */
const DrilldownAboutSection = ({
  label = 'About this metric',
  body,
  sections = [],
  sheetOpen,
  defaultOpen = false,
  /** When true, body/sections mount only while expanded (signup definitions). */
  deferContentUntilOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    if (sheetOpen) setOpen(defaultOpen);
  }, [sheetOpen, defaultOpen]);

  if (!body && (!sections || sections.length === 0)) return null;

  const showContent = !deferContentUntilOpen || open;

  return (
    <Box sx={{ mb: 1.25 }}>
      {open ? (
        <Box
          sx={{
            borderRadius: '8px',
            border: '1px solid rgba(0,0,0,0.06)',
            bgcolor: 'rgba(0,0,0,0.025)',
            p: { xs: 1.5, sm: 2 },
          }}
        >
          <ButtonBase onClick={() => setOpen(false)} sx={expandedTriggerSx}>
            <Typography
              sx={{
                fontSize: '0.85rem',
                fontWeight: 500,
                color: 'rgba(0,0,0,0.75)',
              }}
            >
              {label}
            </Typography>
            <ExpandMoreIcon
              sx={{
                fontSize: 18,
                color: 'rgba(0,0,0,0.4)',
                transform: 'rotate(180deg)',
                transition: 'transform 200ms ease',
              }}
            />
          </ButtonBase>
          {showContent ? <AboutBody body={body} sections={sections} /> : null}
        </Box>
      ) : (
        <ButtonBase
          onClick={() => setOpen(true)}
          sx={DRILLDOWN_COLLAPSE_TRIGGER_SX}
        >
          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: 500,
              color: 'rgba(0,0,0,0.75)',
            }}
          >
            {label}
          </Typography>
          <ExpandMoreIcon
            sx={{
              fontSize: 18,
              color: 'rgba(0,0,0,0.4)',
              transform: 'rotate(0deg)',
              transition: 'transform 200ms ease',
            }}
          />
        </ButtonBase>
      )}
    </Box>
  );
};

export default DrilldownAboutSection;
